import type { ReactNode } from "react";

// Hafif bir Markdown çevirici: asistanın döndürdüğü kısa biçimlendirmeyi
// (kalın, kod, tıklanabilir bağlantı, madde işareti, numaralı liste, kısa başlık,
// satır sonu) güzelce gösterir. Harici bir kütüphane gerektirmez.
//
// onNavigate: dahili linke (yol "/" ile başlar) tıklandığında çağrılır.
// Sağlanmazsa dahili link normal <a href> gibi davranır.

type InlineToken =
  | { type: "text"; value: string }
  | { type: "bold"; value: string }
  | { type: "code"; value: string }
  | { type: "link"; label: string; url: string };

// Satır içi biçimlendirmeyi (**kalın**, `kod` ve [metin](url)) parçalara ayırır.
const INLINE_REGEX = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g;

function tokenizeInline(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = INLINE_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    const raw = match[0];
    if (raw.startsWith("[")) {
      const linkMatch = raw.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        tokens.push({ type: "link", label: linkMatch[1], url: linkMatch[2] });
      } else {
        tokens.push({ type: "text", value: raw });
      }
    } else if (raw.startsWith("**") && raw.endsWith("**")) {
      tokens.push({ type: "bold", value: raw.slice(2, -2) });
    } else if (raw.startsWith("`") && raw.endsWith("`")) {
      tokens.push({ type: "code", value: raw.slice(1, -1) });
    } else {
      tokens.push({ type: "text", value: raw });
    }
    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: "text", value: text.slice(lastIndex) });
  }

  return tokens;
}

function renderInline(
  text: string,
  keyPrefix: string,
  onNavigate?: (url: string) => void,
): ReactNode[] {
  const tokens = tokenizeInline(text);
  return tokens.map((token, i) => {
    const key = `${keyPrefix}-${i}`;
    if (token.type === "bold") {
      return (
        <strong key={key} className="font-semibold text-foreground-950">
          {token.value}
        </strong>
      );
    }
    if (token.type === "code") {
      return (
        <code key={key} className="px-1 py-0.5 rounded bg-background-200/70 font-mono text-[0.95em]">
          {token.value}
        </code>
      );
    }
    if (token.type === "link") {
      const isInternal = token.url.startsWith("/");
      if (isInternal) {
        return (
          <a
            key={key}
            href={token.url}
            onClick={(e) => {
              e.preventDefault();
              onNavigate?.(token.url);
            }}
            className="text-accent-600 font-medium underline underline-offset-2 hover:text-accent-700"
          >
            {token.label}
          </a>
        );
      }
      return (
        <a
          key={key}
          href={token.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-accent-600 font-medium underline underline-offset-2 hover:text-accent-700"
        >
          {token.label}
        </a>
      );
    }
    return <span key={key}>{token.value}</span>;
  });
}

export default function MarkdownText({
  text,
  onNavigate,
}: {
  text: string;
  onNavigate?: (url: string) => void;
}) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const nodes: ReactNode[] = [];
  let listItems: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let keyCounter = 0;

  const flushList = () => {
    if (listItems.length === 0) return;
    if (listType === "ol") {
      nodes.push(
        <ol key={`ol-${keyCounter++}`} className="pl-4 my-1 space-y-1 list-decimal">
          {listItems.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {renderInline(item, `oli-${i}`, onNavigate)}
            </li>
          ))}
        </ol>,
      );
    } else {
      nodes.push(
        <ul key={`ul-${keyCounter++}`} className="pl-4 my-1 space-y-1">
          {listItems.map((item, i) => (
            <li key={i} className="relative pl-3 leading-relaxed">
              <span className="absolute left-0 top-[0.6em] w-1 h-1 rounded-full bg-accent-500" aria-hidden="true"></span>
              {renderInline(item, `uli-${i}`, onNavigate)}
            </li>
          ))}
        </ul>,
      );
    }
    listItems = [];
    listType = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    // Boş satır: listeyi ve paragrafı kapat.
    if (line.trim() === "") {
      flushList();
      continue;
    }

    const trimmed = line.trim();

    // Kısa başlık (### )
    const heading = trimmed.match(/^#{1,4}\s+(.+)$/);
    if (heading) {
      flushList();
      const level = trimmed.match(/^#+/)?.[0].length ?? 3;
      const cls = level <= 2 ? "text-sm font-semibold" : "text-[13px] font-semibold";
      nodes.push(
        <p key={`h-${keyCounter++}`} className={`${cls} mt-2 first:mt-0 text-foreground-950`}>
          {renderInline(heading[1], `h-${keyCounter}`, onNavigate)}
        </p>,
      );
      continue;
    }

    // Madde işaretli liste (- veya * veya •)
    const ul = trimmed.match(/^[-*•]\s+(.+)$/);
    if (ul) {
      if (listType !== "ul") flushList();
      listType = "ul";
      listItems.push(ul[1]);
      continue;
    }

    // Numaralı liste (1. veya 1) )
    const ol = trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (ol) {
      if (listType !== "ol") flushList();
      listType = "ol";
      listItems.push(ol[1]);
      continue;
    }

    // Normal paragraf satırı.
    flushList();
    nodes.push(
      <p key={`p-${keyCounter++}`} className="leading-relaxed">
        {renderInline(trimmed, `p-${keyCounter}`, onNavigate)}
      </p>,
    );
  }

  flushList();

  return <div className="space-y-1">{nodes}</div>;
}